"use client";

import { useState } from "react";
import { Box, Card, Flex, Grid, Link } from "@radix-ui/themes";
import NextLink from "next/link";

import Image from "@/app/components/atoms/Image";
import Accordion from "@/app/components/atoms/Accordion";
import BulletPoints from "@/app/components/atoms/BulletPoints";
import Callout from "@/app/components/atoms/Callout";
import { H1, H2, H3 } from "@/app/components/atoms/Heading";
import Strong from "@/app/components/atoms/Strong";
import TableOfContents from "@/app/components/atoms/TableOfContents";
import Text from "@/app/components/atoms/Text";

import {
  subContentItemsPPLA,
  subSubContentItemsPPLH,
  subContentItemsPPLH,
  subContentItemsLAPLA,
  subContentItemsNPPL,
  calloutTrialFlight,
  calloutCopyMedicalExamination,
  calloutCopyMedicalExaminationPPLH,
  calloutCopyMedicalExaminationLAPL,
  theoreticalKnowledgeBulletPoints,
  theoreticalKnowledgeBulletPointsNPPL,
  calloutFlightTrainingPPLA,
  calloutFlightTrainingPPLA2,
  calloutFlightTrainingLAPLA,
  flightTrainingBulletPoints,
  flightTrainingBulletPointsPPLH,
  flightTrainingBulletPointsLAPL,
  flightTrainingBulletPointsNPPL,
  calloutFlightTrainingPPLH2,
  choosingACourseFAQs,
} from "./config";

export default function PrivatePilot() {
  const [selectedLicense, setSelectedLicense] = useState("PPL");
  return (
    <section>
      <Box maxWidth={{ initial: "768px", sm: "672px" }} mx="auto">
        <Grid gap="4">
          <Image
            alt=""
            isFirst
            isPriority
            src="/1-20141111-GrobTaxi-727NAS-U.jpg"
            isHeader
          />
          <H1>Becoming a Private Pilot</H1>
          <Grid columns="4" gap="2" mt="3" mb="3">
            <Link onClick={() => setSelectedLicense("PPL")}>
              <Card>
                <Flex justify="center" align="center" height="100%">
                  <Strong>PPL(A)</Strong>
                </Flex>
              </Card>
            </Link>
            <Link onClick={() => setSelectedLicense("PPL(H)")}>
              <Card>
                <Flex justify="center" align="center" height="100%">
                  <Strong>PPL(H)</Strong>
                </Flex>
              </Card>
            </Link>
            <Link onClick={() => setSelectedLicense("LAPL")}>
              <Card>
                <Flex justify="center" align="center" height="100%">
                  <Text isCentered>
                    <Strong>LAPL(A)</Strong>
                  </Text>
                </Flex>
              </Card>
            </Link>
            <Link onClick={() => setSelectedLicense("NPPL")}>
              <Card>
                <Flex justify="center" align="center" height="100%">
                  <Text isCentered>
                    <Strong>NPPL(A)</Strong>
                  </Text>
                </Flex>
              </Card>
            </Link>
          </Grid>
          {/* PPL(A) */}
          {selectedLicense === "PPL" && (
            <>
              <H2 id="ppl-a">PPL(A)</H2>
              <TableOfContents contentItems={subContentItemsPPLA} secondary />
              <Text>
                A Private Pilots Licence (PPL) allows you to fly non-commercial
                aircraft and is a great place to start for those who wish to
                embark upon a new hobby or pursue a career in aviation in the
                future. With the relevant experience you can progress a PPL on
                to a Commercial Pilots Licence.
              </Text>
              <Text>
                The Light Aircraft Pilot Licence (LAPL) and National Private
                Pilot Licence (NPPL) are also licence options that fall under
                the private pilot category and are discussed separately. Both
                the LAPL and NPPL have specific requirements and limitations, so
                it is strongly advised to read all three licence types to
                determine which best suits your objectives.
              </Text>
              <H3 id="ppl-a/requirements-and-qualifications">
                Requirements and Qualifications
              </H3>
              <Text>
                No formal qualifications are required to embark upon your
                Private Pilots License, however a good comprehension of GCSE
                level Maths and Physics would be advantageous. English is the
                universal language of aviation, therefore a suitable verbal and
                written proficiency is required.
              </Text>
              <Text>
                You must be at least 17 years of age to obtain your PPL. You are
                permitted to start training prior to this age, however you must
                be 16 to obtain your Part MED Class 2 Medical Certificate and be
                eligible to fly solo. There is no age limit to obtain the
                licence, so long as you are able to meet the medical
                requirements.
              </Text>
              <Image alt="" src="/academic-qualifications-1.jpg" isHeader />
              <H3 id="ppl-a/trial-flight">First Step - Trial Flight</H3>
              <Text>
                Whether a PPL is the start of a challenging and rewarding hobby,
                or the initial stage to embarking upon a flying career, a trial
                flight is the ideal place to start. Obtaining a PPL requires
                significant commitment and time, so confirming whether flying is
                something you will enjoy going forwards is important to
                determine from the outset.
              </Text>
              <Text>
                A trial flight will also allow you to establish whether the
                flying school you have initially selected meets your
                expectations and is a comfortable learning environment. For this
                reason, it is strongly recommended to select a flying school and
                aircraft you are likely to train in for your trial flight.
              </Text>

              <Text>
                Each flying school will offer a trial flight option that will
                consist of you flying with a qualified instructor, who will
                introduce you to the preliminary stages of flying, as well as
                the local airspace and flying school itself.
              </Text>
              <Callout copy={calloutTrialFlight} />
              <Text>
                Whilst there will be a cost attached to booking a trial flight,
                should you commit to obtaining your PPL, the flight-time will
                contribute towards the 40-hour minimum required to obtain the
                licence.
              </Text>
              <Text>
                Trial flights are non-binding so there is nothing stopping you
                having a number of trial flights at various flight schools to
                help you make your decision about where to train. You must
                complete your training course at an Approved Training
                Organisation (ATO) or Declared Training Organisation (DTO).
              </Text>
              <Text>
                <Link href="https://www.caa.co.uk/general-aviation/learning-to-fly/ppl-flying-schools-and-organisations/">
                  Contact a flight school near you
                </Link>
              </Text>
              <H3 id="ppl-a/medical-examination">
                Second Step – Medical Examination
              </H3>
              <Text>
                A medical is not strictly required at this stage, however
                obtaining your Part MED Class 2 Medical Certificate at the
                earliest opportunity will ensure you are not disappointed at a
                later date.
              </Text>
              <Text>
                A Class 2 medical must be completed prior to flying solo and
                conducted by an Aviation Medical Examiner (AME). A list of
                approved AME’s is held by the Civil Aviation Authority (CAA) and
                can be found on their website.
              </Text>
              <Callout copy={calloutCopyMedicalExamination} />
              <Text>
                Providing there are no changes in your health and you are below
                the age of 50, obtaining a Class 1 medical will also provide
                Class 2 medical privileges (required for PPL Flying) for a
                duration of at least two years from the date of issue.
              </Text>
              <H3 id="ppl-a/training">PPL Training</H3>
              {/* <TableOfContents contentItems={subSubContentItemsPPLA} secondary /> */}
              <Text>
                Now that you have taken your trial flight, obtained your medical
                and carefully considered where to complete your training, a
                thorough understanding of what the course entails is essential
                to creating a successful plan.
              </Text>
              <Text>
                A PPL course is largely separated into two sections, theoretical
                knowledge and flight training.
              </Text>
              <Strong id="theoretical-knowledge">Theoretical Knowledge</Strong>
              <Text>
                The theoretical knowledge section of the PPL provides the
                essential foundations for your future in aviation and will
                support all aspects of flight training that you will undertake.
                You will have to study and pass exams in the following subject
                areas:
              </Text>
              <BulletPoints points={theoreticalKnowledgeBulletPoints} />
              <Text>
                Once your theoretical exams are successfully passed, they will
                be valid for 24 months – starting from the date you completed
                your last theoretical examination. Within this 24-month period
                you must complete all aspects of flight training and pass the
                skill test, otherwise these exams will expire and you will have
                to retake them.
              </Text>
              <Strong id="ppl-a/training/flight-radiotelephony-operators-license">
                Flight Radiotelephony Operator’s Licence
              </Strong>
              <Text>
                The Flight Radiotelephony Operator’s Licence (FRTOL) authorises
                you to operate an Aircraft Radio Station in a UK registered
                aircraft and will need to be obtained in conjunction with your
                PPL Flight Crew Licence.
              </Text>
              <Text>
                You must be at least 14 years of age to obtain your FRTOL and
                need to have passed the PPL Communications theoretical
                examination before taking the Radiotelephony practical exam.
              </Text>
              <Text>
                CAP413 is the Radio Telephony Manual by the CAA that will help
                you prepare for the exam.
              </Text>
              <Strong id="ppl-a/training/flight-training">
                Flight Training
              </Strong>
              <Text>
                You will need to complete a minimum of 40 hours for your PPL
                flight training, 5 hours of which may be completed in an
                approved flight simulator, sometimes referred to as a Flight
                Simulation Training Device (FSTD).
              </Text>
              <Callout copy={calloutFlightTrainingPPLA} />
              <Text>
                These lessons are structured and arranged into exercises, which
                cover the basic skills required to safely fly an aircraft. Your
                flight training syllabus must include at least:
              </Text>
              <BulletPoints points={flightTrainingBulletPoints} />
              <Callout copy={calloutFlightTrainingPPLA2} />
              <Strong id="ppl-a/skills-test">Skills Test</Strong>
              <Text>
                At the end of your training, you will need to take a skill test
                with an examiner in the relevant aircraft type to demonstrate
                that you can competently carry out the procedures and manoeuvres
                that you have been taught, while acting as pilot in command
                (PIC).
              </Text>
              <Text>
                You must have had some flight instruction on the same aircraft
                type or class that you will be taking your skill test in.
                Typically you will take the test in the same aircraft in which
                you have been trained.
              </Text>
              <H3 id="ppl-a/cost">Cost</H3>
              <Text>
                The average cost of obtaining a PPL in the United Kingdom is
                £10,956.
              </Text>
              <Text>
                A large number of flying scholarship opportunities are available
                however to ease the financial barrier in to aviation. To find
                out more information visit the FlyingSTART
                <Strong>
                  {" "}
                  <NextLink href="/scholarships" passHref legacyBehavior>
                    <Link>Scholarships page</Link>
                  </NextLink>
                  .
                </Strong>
              </Text>
              <H3 id="ppl-a/beyond-your-ppl">Beyond your PPL</H3>
              <Text>
                Upon passing your skill test and receiving your PPL licence,
                continuing to learn and building your experience is essential.
                You may wish to incorporate new skills as part of this process,
                which can be added to your licence in the form of ratings. The
                most common of these ratings are listed below:
              </Text>
              <Text>
                <Strong>Night Rating</Strong>: This allows you to use the
                privileges of your PPL at night.
              </Text>
              <Text>
                <Strong>Instrument Rating Restricted (IRR)</Strong>: This
                teaches you how to make an instrument approach and fly in
                instrument conditions should you inadvertently find yourself
                flying in these conditions.
              </Text>
              <Text>
                <Strong>Upset Prevention & Recovery Training (UPRT)</Strong>:
                This is a theoretical knowledge and flying training course with
                the aim of providing flight crew with the required competencies
                to recover from aeroplane upsets.
              </Text>
              <Text>
                <Strong>Tail Wheel Rating</Strong>: This allows you to fly
                aircraft with a tail wheel configuration i.e. two wheels at the
                front one at the rear, as opposed to the more familiar tricycle
                gear with one wheel in the front. This opens up the opportunity
                to fly many vintage aircraft.
              </Text>
              <Text>
                <Strong>Floatplane Rating</Strong>: This allows you to fly an
                aircraft with floats replacing the normal wheeled undercarriage,
                enabling you to take off and land on water.
              </Text>
              <Text>
                <Strong>Instrument Rating (IR)</Strong>: This allows you to plan
                a flight in which you would take off, fly and land in
                Instrumental Meteorological Conditions (IMC).
              </Text>
              <Text>
                <Strong>Aerobatic Rating</Strong>: This is a fun way to safely
                learn how to explore the aerobatic abilities of a suitably
                certified aircraft.
              </Text>
              <Text>
                <Strong>Sailplane or Banner Towing Rating</Strong>: This allows
                you to tow sailplanes or banners and enables many to accumulate
                hours for free, or at a significantly subsidised rate.
              </Text>
              <H3 id="ppl-a/career-beyond-your-ppl">
                A Career beyond your PPL
              </H3>
              <Text>
                At this stage you may be considering flying as a career. There
                are a large number of flying opportunities that exist within
                aviation and exploring the various routes to becoming a
                professional pilot is key.
              </Text>
            </>
          )}
          {/* PPL(H) */}
          {selectedLicense === "PPL(H)" && (
            <>
              <H2 id="ppl-h">PPL(H)</H2>
              <TableOfContents contentItems={subContentItemsPPLH} secondary />
              <Text>
                A Helicopter Private Pilots Licence (PPL(H)) allows you to fly
                non-commercial aircraft and is a great place to start for those
                who wish to embark upon a new hobby or pursue a career in
                aviation in the future. With the relevant experience you can
                progress a PPL(H) on to a Commercial Pilots Licence.
              </Text>
              <H3 id="ppl-h/requirements-and-qualifications">
                Requirements and Qualifications
              </H3>
              <Text>
                No formal qualifications are required to embark upon your
                Private Pilots License, however a good comprehension of GCSE
                level Maths and Physics would be advantageous. English is the
                universal language of aviation, therefore a suitable verbal and
                written proficiency is required.
              </Text>
              <Text>
                You must be at least 17 years of age to obtain your PPL(H). You
                are permitted to start training prior to this age, however you
                must be 16 to obtain your Part MED Class 2 Medical Certificate
                and be eligible to fly solo. There is no age limit to obtain the
                licence, so long as you are able to meet the medical
                requirements.
              </Text>
              <Image alt="" src="/academic-qualifications-1.jpg" isHeader />
              <H3 id="ppl-h/trial-flight">First Step - Trial Flight</H3>
              <Text>
                Whether a PPL(H) is the start of a challenging and rewarding
                hobby, or the initial stage to embarking upon a flying career, a
                trial flight is the ideal place to start. Obtaining a PPL(H)
                requires significant commitment and time, so confirming whether
                flying is something you will enjoy going forwards is important
                to determine from the outset.
              </Text>
              <Text>
                A trial flight will also allow you to establish whether the
                flying school you have initially selected meets your
                expectations and is a comfortable learning environment. For this
                reason, it is strongly recommended to select a flying school and
                aircraft you are likely to train in for your trial flight.
              </Text>
              <Text>
                Each flying school will offer a trial flight option that will
                consist of you flying with a qualified instructor, who will
                introduce you to the preliminary stages of flying, as well as
                the local airspace and flying school itself.
              </Text>
              <Callout copy={calloutTrialFlight} />
              <Text>
                Whilst there will be a cost attached to booking a trial flight,
                should you commit to obtaining your PPL(H), the flight-time will
                contribute towards the 40-hour minimum required to obtain the
                licence.
              </Text>
              <Text>
                Trial flights are non-binding so there is nothing stopping you
                having a number of trial flights at various flight schools to
                help you make your decision about where to train. You must
                complete your training course at an Approved Training
                Organisation (ATO) or Declared Training Organisation (DTO).
              </Text>
              <Text>
                <Link href="https://www.caa.co.uk/general-aviation/learning-to-fly/ppl-flying-schools-and-organisations/">
                  Contact a flight school near you
                </Link>
              </Text>
              <H3 id="ppl-h/medical-examination">
                Second Step – Medical Examination
              </H3>
              {/* <TableOfContents contentItems={subSubContentItemsPPLH} secondary /> */}
              <Text>
                A medical is not strictly required at this stage, however
                obtaining your Part MED Class 2 Medical Certificate at the
                earliest opportunity will ensure you are not disappointed at a
                later date.
              </Text>
              <Text>
                A Class 2 medical must be completed prior to flying solo and
                conducted by an Aviation Medical Examiner (AME). A list of
                approved AME’s is held by the Civil Aviation Authority (CAA) and
                can be found on their website.
              </Text>
              <Callout copy={calloutCopyMedicalExaminationPPLH} />
              <Text>
                Providing there are no changes in your health and you are below
                the age of 50, obtaining a Class 1 medical will also provide
                Class 2 medical privileges (required for PPL(H) Flying) for a
                duration of at least two years from the date of issue.
              </Text>
              <H3 id="ppl-h/training">PPL(H) Training</H3>
              <Text>
                Now that you have taken your trial flight, obtained your medical
                and carefully considered where to complete your training, a
                thorough understanding of what the course entails is essential
                to creating a successful plan.
              </Text>
              <Text>
                A PPL(H) course is largely separated into two sections,
                theoretical knowledge and flight training.
              </Text>
              <Strong id="/theoretical-knowledge">Theoretical Knowledge</Strong>
              <Text>
                The theoretical knowledge section of the PPL(H) provides the
                essential foundations for your future in aviation and will
                support all aspects of flight training that you will undertake.
                You will have to study and pass exams in the following subject
                areas:
              </Text>
              <BulletPoints points={theoreticalKnowledgeBulletPoints} />
              <Text>
                Once your theoretical exams are successfully passed, they will
                be valid for 24 months – starting from the date you completed
                your last theoretical examination. Within this 24-month period
                you must complete all aspects of flight training and pass the
                skill test, otherwise these exams will expire and you will have
                to retake them.
              </Text>
              <Strong id="/flight-radiotelephony-operators-license">
                Flight Radiotelephony Operator’s Licence
              </Strong>
              <Text>
                The Flight Radiotelephony Operator’s Licence (FRTOL) authorises
                you to operate an Aircraft Radio Station in a UK registered
                aircraft and will need to be obtained in conjunction with your
                PPL(H) Flight Crew Licence.
              </Text>
              <Text>
                You must be at least 14 years of age to obtain your FRTOL and
                need to have passed the PPL Communications theoretical
                examination before taking the Radiotelephony practical exam.
              </Text>
              <Text>
                CAP413 is the Radio Telephony Manual by the CAA that will help
                you prepare for the exam.
              </Text>
              <Strong id="/flight-training">Flight Training</Strong>
              <TableOfContents
                contentItems={subSubContentItemsPPLH}
                secondary
              />
              <Text>
                You will need to complete a minimum of 45 hours for your PPL(H)
                flight training, 5 hours of which may be completed in an
                approved flight simulator, sometimes referred to as a Flight
                Simulation Training Device (FSTD).
              </Text>
              <Callout copy={calloutFlightTrainingPPLA} />
              <Text>
                These lessons are structured and arranged into exercises, which
                cover the basic skills required to safely fly an aircraft. Your
                flight training syllabus must include at least:
              </Text>
              <BulletPoints points={flightTrainingBulletPointsPPLH} />
              <Callout copy={calloutFlightTrainingPPLH2} />
              <Strong id="ppl-h/skills-test">Skills Test</Strong>
              <Text>
                At the end of your training, you will need to take a skill test
                with an examiner in the relevant aircraft type to demonstrate
                that you can competently carry out the procedures and manoeuvres
                that you have been taught, while acting as pilot in command
                (PIC).
              </Text>
              <H3 id="ppl-h/cost">Cost</H3>
              <Text>
                The average cost of obtaining a PPL(H) in the United Kingdom is
                £25,000-£30,000.
              </Text>
              <Text>
                A small number of flying scholarship opportunities are available
                however to ease the financial barrier in to aviation. To find
                out more information visit the FlyingSTART
                <Strong>
                  {" "}
                  <NextLink href="/scholarships" passHref legacyBehavior>
                    <Link>Scholarships page</Link>
                  </NextLink>
                  .
                </Strong>
              </Text>
              <H3 id="ppl-h/beyond-your-ppl">Beyond your PPL(H)</H3>
              <Text>
                Upon passing your skill test and receiving your PPL(H) licence,
                continuing to learn and building your experience is essential.
                You may wish to incorporate new skills as part of this process,
                which can be added to your licence in the form of ratings. The
                most common of these ratings are listed below:
              </Text>
              <Text>
                <Strong>Night Rating</Strong>: This allows you to use the
                privileges of your PPL(H) at night. This requires 100 hours of
                flight time post Skill Test completion and licence issue.
              </Text>
              <Text>
                <Strong>Mountain Rating</Strong>: Select locations offer
                mountain flying training. The training includes theoretical
                instruction, flying through valleys and landing on designated
                mountain sites. Pilots learn to adapt to changing weather
                conditions, recognise the helicopter’s performance limits and
                make good landing decisions.
              </Text>
              <Text>
                <Strong>Instrument Rating (IR)</Strong>: This allows you to plan
                a flight in which you would take off, fly and land in
                Instrumental Meteorological Conditions (IMC).
              </Text>
              <H3 id="ppl-a/career-beyond-your-ppl">
                A Career beyond your PPL(H)
              </H3>
              <Text>
                At this stage you may be considering flying as a career. There
                are a large number of flying opportunities that exist within
                aviation and exploring the various routes to becoming a
                professional pilot is key.
              </Text>
              <Accordion content={choosingACourseFAQs} />
            </>
          )}
          {/* LAPL */}
          {selectedLicense === "LAPL" && (
            <>
              <H2 id="lapl-a">LAPL(A)</H2>
              <TableOfContents contentItems={subContentItemsLAPLA} secondary />
              <Text>
                A Light Aircraft Pilot Licence (LAPL) allows you to fly
                non-commercial single-engine aircraft below a take-off mass of
                2000kg in the United Kingdom and is a great place to start for
                those who wish to embark upon a new hobby or pursue a career in
                aviation in the future. With the relevant experience you can
                progress a LAPL on to a Private Pilots Licence and eventually a
                Commercial Pilots Licence.
              </Text>
              <Text>
                The Private Pilots Licence (PPL) and National Private Pilots
                Licence (NPPL) are also licence options that fall under the
                private pilot category and are discussed separately. Both the
                PPL and NPPL have specific requirements and limitations, so it
                is strongly advised to read all three licence types to determine
                which best suits your objectives.
              </Text>
              <H3 id="lapl-a/requirements-and-qualifications">
                Requirements and Qualifications
              </H3>
              <Text>
                No formal qualifications are required to embark upon your Light
                Aircraft Pilot Licence, however a good comprehension of GCSE
                level Maths and Physics would be advantageous. English is the
                universal language of aviation, therefore suitable verbal and
                written proficiency is required.
              </Text>
              <Text>
                You must be at least 17 years of age to obtain your LAPL. You
                are permitted to start training prior to this age, however you
                must be 16 to obtain your Part MED LAPL Medical Certificate and
                be eligible to fly solo. There is no age limit to obtain the
                licence, so long as you are able to meet the medical
                requirements.
              </Text>
              <Image alt="" src="/academic-qualifications-1.jpg" isHeader />
              <H3 id="lapl-a/trial-flight">First Step - Trial Flight</H3>
              <Text>
                Whether a LAPL is the start of a challenging and rewarding
                hobby, or the initial stage to embarking upon a flying career, a
                trial flight is the ideal place to start. Obtaining a LAPL
                requires significant commitment and time, so confirming whether
                flying is something you will enjoy going forwards is important
                to determine from the outset.
              </Text>
              <Text>
                A trial flight will also allow you to establish whether the
                flying school you have initially selected meets your
                expectations and is a comfortable learning environment. For this
                reason, it is strongly recommended to select a flying school and
                aircraft you are likely to train in for your trial flight.
              </Text>
              <Text>
                Each flying school will offer a trial flight option that will
                consist of you flying with a qualified instructor, who will
                introduce you to the preliminary stages of flying, as well as
                the local airspace and flying school itself.
              </Text>
              <Callout copy={calloutTrialFlight} />
              <Text>
                Whilst there will be a cost attached to booking a trial flight,
                should you commit to obtaining your LAPL, the flight-time will
                contribute towards the 30-hour minimum required to obtain the
                licence.
              </Text>
              <Text>
                Trial flights are non-binding so there is nothing stopping you
                having a number of trial flights at various flight schools to
                help you make your decision about where to train. You must
                complete your training course at an Approved Training
                Organisation (ATO) or Declared Training Organisation (DTO).
              </Text>
              <H3 id="lapl-a/medical-examination">
                Second Step – Medical Examination
              </H3>
              <Text>
                A medical is not strictly required at this stage, however
                obtaining your Part MED LAPL Medical Certificate at the earliest
                opportunity will ensure you are not disappointed at a later
                date.
              </Text>
              <Text>
                A LAPL Medical must be completed prior to flying solo and should
                be conducted by an Aviation Medical Examiner (AME). A list of
                approved AME’s is held by the Civil Aviation Authority (CAA) and
                can be found on their website.
              </Text>
              <Callout copy={calloutCopyMedicalExaminationLAPL} />
              <Text>
                Providing there are no changes in your health, obtaining a Class
                1 medical will also provide LAPL medical privileges for a
                duration of at least two years from the date of issue.
              </Text>
              <H3 id="lapl-a/training">LAPL Training</H3>
              <Text>
                Now that you have taken your trial flight, obtained your LAPL
                medical and carefully considered where to complete your
                training, a thorough understanding of what the course entails is
                essential to creating a successful plan.
              </Text>
              <Text>
                A LAPL course is largely separated into two sections,
                theoretical knowledge and flight training.
              </Text>
              <BulletPoints points={theoreticalKnowledgeBulletPoints} />
              <Text>
                Once your theoretical exams are successfully passed, they will
                be valid for 24 months – starting from the date you completed
                your last theoretical examination. Within this 24-month period
                you must complete all aspects of flight training and pass the
                skill test, otherwise these exams will expire and you will have
                to retake them.
              </Text>
              <Strong id="lapl-a/training/flight-radiotelephony-operators-license">
                Flight Radiotelephony Operator’s Licence
              </Strong>
              <Text>
                The Flight Radiotelephony Operator’s Licence (FRTOL) authorises
                you to operate an Aircraft Radio Station in a UK registered
                aircraft and will need to be obtained in conjunction with your
                LAPL Flight Crew Licence.
              </Text>
              <Text>
                You must be at least 14 years of age to obtain your FRTOL and
                need to have passed the LAPL Communications theoretical
                examination before taking the Radiotelephony practical exam.
              </Text>
              <Text>
                CAP413 is the Radio Telephony Manual by the CAA that will help
                you prepare for the exam.
              </Text>
              <Strong id="lapl-a/training/flight-training">
                Flight Training
              </Strong>
              <Text>
                You will need to complete a minimum of 30 hours for your LAPL
                flight training. These lessons are structured and arranged into
                exercises, which cover the basic skills required to safely fly
                an aircraft. Your flight training syllabus must include at
                least:
              </Text>
              <BulletPoints points={flightTrainingBulletPointsLAPL} />
              <Callout copy={calloutFlightTrainingLAPLA} />
              <Strong id="lapl-a/skills-test">Skills Test</Strong>
              <Text>
                At the end of your training, you will need to take a skills test
                with an examiner in the relevant aircraft type to demonstrate
                that you can competently carry out the procedures and manoeuvres
                that you have been taught, while acting as pilot in command
                (PIC).
              </Text>
              <Text>
                You must have had some flight instruction on the same aircraft
                type or class that you will be taking your skill test in.
                Typically you will take the test in the same aircraft in which
                you have been trained.
              </Text>
              <H3 id="lapl-a/cost">Cost</H3>
              <Text>
                The average cost of obtaining a LAPL in the United Kingdom is
                £7,165.
              </Text>
              <Text>
                A large number of flying scholarship opportunities are available
                however to ease the financial barrier in to aviation. To find
                out more information visit the FlyingSTART
                <Strong>
                  {" "}
                  <NextLink href="/scholarships" passHref legacyBehavior>
                    <Link>Scholarships page</Link>
                  </NextLink>
                  .
                </Strong>
              </Text>
              <H3 id="lapl-a/career-beyond-your-lapl">
                A Career beyond your LAPL
              </H3>
              <Text>
                At this stage you may be considering flying as a career. There
                are a large number of flying opportunities that exist within
                aviation and exploring the various routes to becoming a
                professional pilot is key.
              </Text>
            </>
          )}
          {/* NPPL */}
          {selectedLicense === "NPPL" && (
            <>
              <H2 id="nppl-a">NPPL(A)</H2>
              <TableOfContents contentItems={subContentItemsNPPL} secondary />
              <Text>
                A National Private Pilots Licence (NPPL) allows you to fly
                microlight aircraft in the United Kingdom and is a great place
                to start for those who wish to embark upon a new hobby.
              </Text>
              <Text>
                The Private Pilots Licence (PPL) and Light Aircraft Pilot
                Licence (LAPL) are also licence options that fall under the
                private pilot category and are discussed separately. Both the
                PPL and LAPL have specific requirements and limitations, so it
                is strongly advised to read all three licence types to determine
                which best suits your objectives.
              </Text>
              <H3 id="nppl/requirements-and-qualifications">
                Requirements and Qualifications
              </H3>
              <Text>
                No formal qualifications are required to embark upon your
                National Private Pilots License, however a good comprehension of
                GCSE level Maths and Physics would be advantageous. English is
                the universal language of aviation, therefore a suitable verbal
                and written proficiency is required.
              </Text>
              <Text>
                You must be at least 17 years of age to obtain your NPPL. You
                are permitted to start training at 14 years of age, however you
                must be 16 to be eligible to fly solo. There is no age limit to
                obtain the licence, so long as you are able to meet the medical
                requirements.
              </Text>
              <Image alt="" src="/academic-qualifications-1.jpg" isHeader />
              <H3 id="nppl/trial-flight">First Step - Trial Flight</H3>
              <Text>
                To determine whether a NPPL is the start of a challenging and
                rewarding hobby, a trial flight is the ideal place to start.
                Obtaining a NPPL requires significant commitment and time, so
                confirming whether flying is something you will enjoy going
                forwards is important to determine from the outset.
              </Text>
              <Text>
                A trial flight will also allow you to establish whether the
                flying school you have initially selected meets your
                expectations and is a comfortable learning environment. For this
                reason, it is strongly recommended to select a flying school and
                aircraft you are likely to train in for your trial flight.
              </Text>
              <Text>
                Each flying school will offer a trial flight option that will
                consist of you flying with a qualified instructor, who will
                introduce you to the preliminary stages of flying, as well as
                the local airspace and flying school itself.
              </Text>
              <Callout copy={calloutTrialFlight} />
              <Text>
                Whilst there will be a cost attached to booking a trial flight,
                should you commit to obtaining your NPPL, the flight-time will
                contribute towards the minimum hours required to obtain the
                licence.
              </Text>
              <Text>
                Trial flights are non-binding so there is nothing stopping you
                having a number of trial flights at various flight schools to
                help you make your decision about where to train.{" "}
              </Text>
              <H3 id="nppl/medical-examination">
                Second Step – Medical Examination
              </H3>
              <Text>
                A medical is not strictly required at this stage, however
                obtaining your Medical Declaration at the earliest opportunity
                will ensure you are not disappointed at a later date.
              </Text>
              <Text>
                A Medical Declaration must be attained prior to flying solo and
                should be conducted by an Aviation Medical Examiner (AME). A
                list of approved AME’s is held by the Civil Aviation Authority
                (CAA) and can be found on their website.
              </Text>
              <H3 id="nppl/training">NPPL Training</H3>
              <Text>
                Now that you have taken your trial flight, obtained your medical
                declaration and carefully considered where to complete your
                training, a thorough understanding of what the course entails is
                essential to creating a successful plan.
              </Text>
              <Text>
                A NPPL course is largely separated into two sections,
                theoretical knowledge and flight training.
              </Text>
              <Strong id="/theoretical-knowledge">Theoretical Knowledge</Strong>
              <Text>
                The theoretical knowledge section of the NPPL provides the
                essential foundations for your future in aviation and will
                support all aspects of flight training that you will undertake.
                You will have to study and pass exams in the following subject
                areas:
              </Text>
              <BulletPoints points={theoreticalKnowledgeBulletPointsNPPL} />
              <Text>
                Once your theoretical exams are successfully passed, they will
                be valid for 24 months – starting from the date you completed
                your last theoretical examination. Within this 24-month period
                you must complete all aspects of flight training and pass the
                General Skills Test (GST), otherwise these exams will expire and
                you will have to retake them.
              </Text>
              <Strong id="nppl/training/flight-radiotelephony-operators-license">
                Flight Radiotelephony Operator’s Licence
              </Strong>
              <Text>
                The Flight Radiotelephony Operator’s Licence (FRTOL) authorises
                you to operate an Aircraft Radio Station in a UK registered
                aircraft and will need to be obtained in conjunction with your
                NPPL Licence.
              </Text>
              <Text>
                You must be at least 14 years of age to be eligible take the
                Radiotelephony practical exam and obtain your FRTOL.
              </Text>
              <Text>
                CAP413 is the Radio Telephony Manual by the CAA that will help
                you prepare for the exam.
              </Text>
              <Strong id="nppl/training/flight-training">
                Flight Training
              </Strong>
              <Text>
                You will need to complete a minimum of 35 hours for your NPPL
                flight training in order to carry passengers and operate in
                conditions of less then 10 kilometres of visibility.
              </Text>
              <Text>
                These lessons are structured and arranged into exercises, which
                cover the basic skills required to safely fly an aircraft. Your
                flight training syllabus must include at least:
              </Text>
              <BulletPoints points={flightTrainingBulletPointsNPPL} />
              <Callout copy={calloutFlightTrainingPPLA} />
              <Strong id="nppl-a/skills-test">General Skills Test</Strong>
              <Text>
                At the end of your training, you will need to take a General
                Skills Test (GST) with an examiner in the relevant aircraft type
                to demonstrate that you can competently carry out the procedures
                and manoeuvres that you have been taught, while acting as pilot
                in command (PIC).
              </Text>
              <Text>
                You must have had some flight instruction on the same aircraft
                type or class that you will be taking your skill test in.
                Typically you will take the test in the same aircraft in which
                you have been trained.
              </Text>
              <Text>
                The flight time of the GST can count towards the minimum total
                flight time required to obtain the NPPL, but not towards the
                solo minimum time.
              </Text>
              <H3 id="nppl/cost">Cost</H3>
              <Text>
                The average cost of obtaining a LAPL in the United Kingdom is
                £6,280.
              </Text>
              <Text>
                A large number of flying scholarship opportunities are available
                however to ease the financial barrier in to aviation. To find
                out more information visit the FlyingSTART
                <Strong>
                  {" "}
                  <NextLink href="/scholarships" passHref legacyBehavior>
                    <Link>Scholarships page</Link>
                  </NextLink>
                  .
                </Strong>
              </Text>
              <H3 id="nppl/career-beyond-your-nppl">
                A Career beyond your NPPL
              </H3>
              <Text>
                At this stage you may be considering flying as a career. There
                are a large number of flying opportunities that exist within
                aviation and exploring the various routes to becoming a
                professional pilot is key.
              </Text>
            </>
          )}
        </Grid>
      </Box>
    </section>
  );
}
